import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { offersApi, offersClient, JUDGMENT_META } from "../lib/offersApi";
import MarginTrendChart from "./MarginTrendChart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

/** Re-usable inline Radix tooltip with the ⓘ icon. R4 FIX J. */
function InfoTooltip({ text, testId }) {
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex cursor-help"
            data-testid={testId}
            tabIndex={0}
          >
            <Info
              className="h-3 w-3"
              style={{ color: "var(--text-muted)" }}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-[320px] text-[12px] leading-snug whitespace-pre-line"
          style={{ background: "#0f172a", color: "white" }}
          side="left"
          sideOffset={6}
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Commercial parameters + margin + shipping side panel.
 * Mounts inside the cliente sidebar during a live offer.
 */
export default function CommercialPanel({ sessionId, session, onUpdate }) {
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState(session?.customer_params || {});
  const [margin, setMargin] = useState(session?.margin_calc || null);
  const [savingField, setSavingField] = useState(null);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    offersApi.meta().then(setMeta).catch(() => {});
  }, []);

  useEffect(() => {
    setParams(session?.customer_params || {});
    setMargin(session?.margin_calc || null);
    // Fetch customer memory (best-effort by domain)
    const cliente = session?.cliente || {};
    const email = cliente.email || cliente.email_domain;
    let domain = null;
    if (email && String(email).includes("@")) {
      domain = String(email).split("@").pop().toLowerCase();
    } else if (cliente.nome_azienda) {
      const token = String(cliente.nome_azienda).toLowerCase().split(" ")[0];
      if (token) domain = `${token.replace(/[^a-z]/g, "")}.it`;
    }
    if (!domain) {
      setCustomer(null);
      return;
    }
    offersClient
      .get(`/customers/${encodeURIComponent(domain)}`)
      .then((r) => setCustomer(r.data.customer))
      .catch(() => setCustomer(null));
  }, [session?.session_id]);

  const updateParam = useCallback(
    async (code, value) => {
      if (!sessionId) return;
      setSavingField(code);
      try {
        const nextParams = { ...params, [code]: value };
        setParams(nextParams);
        const res = await offersApi.updateCustomerParams(sessionId, { [code]: value });
        setParams(res.customer_params);
        setMargin(res.margin_calc);
        onUpdate?.({ customer_params: res.customer_params, margin_calc: res.margin_calc, value_total: res.value_total });
      } catch (e) {
        toast.error("Errore salvataggio parametro");
      } finally {
        setSavingField(null);
      }
    },
    [sessionId, params, onUpdate]
  );

  const setAppliedMarkup = useCallback(
    async (applied) => {
      if (!sessionId) return;
      try {
        const res = await offersApi.markupOverride(sessionId, applied);
        setMargin(res.margin_calc);
      } catch (e) {
        // silent
      }
    },
    [sessionId]
  );

  if (!meta) {
    return (
      <div className="flex items-center gap-2 text-[11px] p-3" style={{ color: "var(--text-muted)" }}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Caricamento parametri…
      </div>
    );
  }

  const approvalTh = meta.approval_threshold_eur || 3000;
  const valueTotal = session?.value_total || 0;
  const requiresApproval = margin?.requires_approval;

  return (
    <div className="space-y-4" data-testid="commercial-panel">
      {/* Storia cliente (TASK 11) — visible only when we have aggregated data */}
      {customer && customer.offers_count > 0 && (
        <div data-testid="customer-history">
          <div className="tq-section-label mb-2">Storia cliente</div>
          <div
            className="rounded-md p-3 space-y-2 text-[11px]"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div
                  className="text-[12px] font-semibold"
                  style={{ color: "var(--text-primary)" }}
                >
                  {customer.company_name || customer.email_domain}
                </div>
                <div style={{ color: "var(--text-muted)" }}>
                  {customer.offers_count} offert{customer.offers_count === 1 ? "a" : "e"} ·{" "}
                  Vinto: € {(customer.total_value_won || 0).toFixed(0)}
                </div>
              </div>
              {customer.last_offer_at && (
                <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  ultima: {new Date(customer.last_offer_at).toLocaleDateString("it-IT")}
                </div>
              )}
            </div>
            {Object.keys(customer.default_customer_params || {}).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(customer.default_customer_params).map(
                  ([code, val]) => (
                    <button
                      key={code}
                      type="button"
                      className="inline-flex items-center gap-1 px-1.5 rounded-full text-[10px]"
                      style={{
                        background: "#DBEAFE",
                        color: "#1D4ED8",
                        border: "1px solid #93C5FD",
                      }}
                      onClick={() => updateParam(code, val)}
                      title={`Applica "${val}" come default`}
                      data-testid={`pref-${code}`}
                    >
                      <Star className="h-2.5 w-2.5" />
                      {val}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 10 commercial params */}
      <div>
        <div className="tq-section-label mb-2">Parametri commerciali</div>
        <div className="space-y-2">
          {meta.commercial_params.map((p) => {
            if (p.kind === "number_readonly") {
              return (
                <div key={p.code} className="space-y-0.5">
                  <div className="flex items-center justify-between gap-1">
                    <label
                      className="text-[11px]"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {p.label}
                    </label>
                    <InfoTooltip text={p.tooltip} testId={`tooltip-${p.code}`} />
                  </div>
                  <div
                    className="tq-cell-mono text-[13px] px-2 py-1 rounded"
                    style={{
                      background: valueTotal > approvalTh ? "#FEF3C7" : "var(--bg-subtle)",
                      color: valueTotal > approvalTh ? "#B45309" : "var(--text-primary)",
                      fontWeight: 600,
                    }}
                    data-testid={`param-${p.code}`}
                  >
                    € {valueTotal.toFixed(2)}
                    {valueTotal > approvalTh && (
                      <span className="ml-2 text-[10px] font-medium">› soglia approvazione</span>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div key={p.code} className="space-y-0.5">
                <div className="flex items-center gap-1">
                  <label
                    className="text-[11px] flex-1 truncate"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {p.label}
                  </label>
                  <InfoTooltip text={p.tooltip} testId={`tooltip-${p.code}`} />
                </div>
                <select
                  value={params[p.code] || ""}
                  onChange={(e) => updateParam(p.code, e.target.value)}
                  disabled={savingField === p.code}
                  className="w-full h-7 px-2 rounded text-[12px]"
                  style={{ border: "1px solid var(--border-default)", background: "var(--bg-surface)" }}
                  data-testid={`param-${p.code}`}
                >
                  <option value="">—</option>
                  {p.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Margin calculator */}
      {margin && margin.scenario_id && (
        <div>
          <div className="tq-section-label mb-2">Calcolatore margine</div>
          <div
            className="rounded-md p-3 space-y-2"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-subtle)",
            }}
            data-testid="margin-panel"
          >
            <div className="flex items-baseline justify-between">
              <span
                className="text-[11px]"
                style={{ color: "var(--text-muted)" }}
                title={margin.scenario_description}
              >
                Scenario: {margin.scenario_id}
              </span>
              <JudgmentBadge judgment={margin.judgment} />
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-[11px]">
              <Stat label="Min" value={`${margin.minimum}%`} />
              <Stat label="Target" value={`${margin.recommended}%`} tone="primary" />
              <Stat label="Ottimale" value={`${margin.ottimale}%`} />
            </div>
            {/* v0.4.3 FIX E — dual pricing: ricarico + margine su PV */}
            <div className="grid grid-cols-2 gap-1.5 pt-1" data-testid="pricing-dual-cards">
              <PriceCard
                title="Ricarico applicato"
                subtitle="(sul costo di acquisto)"
                value={`${(margin.ricarico_costo_pct ?? margin.applied ?? 0).toFixed(1)}%`}
                detail={`Costo 100 € → PV ${(100 * (1 + (margin.applied || 0) / 100)).toFixed(2)} €`}
                tooltip="Quanto aggiungi al costo di acquisto. Listino H&C = costi. Il prezzo al cliente è costo + ricarico."
                testId="pricing-card-ricarico"
                accent="primary"
              />
              <PriceCard
                title="Margine su PV"
                subtitle="(sul prezzo al cliente)"
                value={`${(margin.margine_pv_pct ?? margin.margin_on_pv_pct ?? 0).toFixed(1)}%`}
                detail="Quanto guadagni sul finale"
                tooltip="Quanto guadagni sul prezzo finale. Calcolato come (PV − costo) / PV. È sempre minore del ricarico."
                testId="pricing-card-margine"
                accent="muted"
              />
            </div>
            {/* Applied slider */}
            <div className="space-y-1 pt-1">
              <div className="flex items-center justify-between text-[11px]">
                <span style={{ color: "var(--text-secondary)" }}>Applicato</span>
                <span
                  className="tq-cell-mono font-semibold"
                  style={{ color: "var(--text-primary)" }}
                >
                  {margin.applied?.toFixed?.(1)}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={Math.max(margin.ottimale * 1.5, 60)}
                step={1}
                value={margin.applied || margin.recommended}
                onChange={(e) => setAppliedMarkup(parseFloat(e.target.value))}
                className="w-full"
                data-testid="markup-applied-slider"
              />
              <div
                className="text-[10px]"
                style={{ color: "var(--text-muted)" }}
              >
                Su costo 100 € → prezzo vendita {(100 * (1 + margin.applied / 100)).toFixed(2)} € · margine su PV {margin.margin_on_pv_pct?.toFixed?.(1)}%
              </div>
            </div>
            {margin.adjustments?.length > 0 && (
              <details className="text-[10px]">
                <summary style={{ color: "var(--text-muted)" }}>
                  {margin.adjustments.length} aggiustamenti
                </summary>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {margin.adjustments.map((a, i) => (
                    <li key={i} style={{ color: "var(--text-secondary)" }}>{a}</li>
                  ))}
                </ul>
              </details>
            )}
            <div className="pt-1 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <div
                className="text-[10px] uppercase tracking-wide font-medium mb-1"
                style={{ color: "var(--text-muted)" }}
              >
                Andamento team (30gg)
              </div>
              <MarginTrendChart days={30} target={margin.recommended} height={120} />
            </div>
            {requiresApproval && (
              <div
                className="flex items-start gap-2 rounded px-2 py-2 text-[11px]"
                style={{
                  background: "#FFFBEB",
                  border: "1px solid #FDE68A",
                  color: "#92400E",
                }}
                data-testid="approval-alert"
              >
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                Soglia {approvalTh.toFixed(0)} € superata: richiedere approvazione del titolare prima di inviare al cliente.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shipping moved to InputPane (R5 P1 #4) */}
    </div>
  );
}

function JudgmentBadge({ judgment }) {
  if (!judgment) return null;
  const j = JUDGMENT_META[judgment] || {
    bg: "var(--bg-subtle)",
    fg: "var(--text-secondary)",
  };
  return (
    <span
      className="inline-flex items-center px-1.5 rounded text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: j.bg, color: j.fg }}
      data-testid="margin-judgment"
    >
      {judgment.split(" ")[0]}
    </span>
  );
}

function Stat({ label, value, tone }) {
  const bg = tone === "primary" ? "var(--accent-subtle)" : "var(--bg-subtle)";
  const color = tone === "primary" ? "var(--accent-primary)" : "var(--text-primary)";
  return (
    <div
      className="rounded px-2 py-1 text-center"
      style={{ background: bg }}
    >
      <div
        className="text-[9px] uppercase tracking-wide"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </div>
      <div
        className="tq-cell-mono text-[12px] font-semibold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

function PriceCard({ title, subtitle, value, detail, tooltip, testId, accent }) {
  const isPrimary = accent === "primary";
  return (
    <div
      className="rounded px-2.5 py-2"
      style={{
        background: isPrimary ? "var(--accent-subtle)" : "var(--bg-subtle)",
        border: "1px solid var(--border-subtle)",
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-1">
        <div
          className="text-[10px] uppercase tracking-wide font-medium flex-1"
          style={{ color: "var(--text-muted)" }}
        >
          {title}
        </div>
        <InfoTooltip text={tooltip} testId={`${testId}-tooltip`} />
      </div>
      <div
        className="tq-cell-mono text-[18px] font-semibold leading-none mt-1"
        style={{ color: isPrimary ? "var(--accent-primary)" : "var(--text-primary)" }}
      >
        {value}
      </div>
      {subtitle && (
        <div
          className="text-[10px] mt-0.5"
          style={{ color: "var(--text-muted)" }}
        >
          {subtitle}
        </div>
      )}
      {detail && (
        <div
          className="text-[10px] mt-0.5 truncate"
          style={{ color: "var(--text-secondary)" }}
        >
          {detail}
        </div>
      )}
    </div>
  );
}
